import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AuthCard from './components/AuthCard';
import PasswordInput from './components/PasswordInput';
import { showToastError } from './components/ToastNotifications';

export default function LoginPage({ onSignIn, onCreateAccount, onForgot, error, busy }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [timeout] = useState(searchParams.get('to'));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitPending, setSubmitPending] = useState(false);
  const timeoutHandledRef = useRef(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy || submitPending) return;

    setSubmitPending(true);
    try {
      await onSignIn?.({ email, password });
    } finally {
      setSubmitPending(false);
    }
  };

  const isSubmitBusy = busy || submitPending;

  useEffect(() => {
    if (timeout === '1' && !timeoutHandledRef.current) {
      timeoutHandledRef.current = true;
      showToastError('Your session has expired. Please log in again.');
      setSearchParams({ to: '0' }, { replace: true });
    }
  }, [setSearchParams, timeout]);

  return (
    <AuthCard
      pageTestId="page-login"
      cardTestId="login-form"
      ariaLabel="Sign in"
      title="Retail ID"
      slogan="A single source of truth on every legal product."
      subtitle="Sign in to manage your product information, labels, and QR codes."
    >
      <form className="login-form" onSubmit={handleSubmit}>
          <label className="label" htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            className="input"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSubmitBusy}
            data-testid="login-email"
          />

          <label className="label" htmlFor="login-password">Password</label>
          <PasswordInput
            id="login-password"
            className="input"
            name="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isSubmitBusy}
            autoComplete="current-password"
            data-testid="login-password"
            toggleTestId="login-password-toggle"
          />

          {error && (
            <div className="alert error" role="alert" style={{ marginTop: 4 }} data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="button cta"
            style={{ width: '100%', marginTop: 8 }}
            data-testid="login-submit"
            disabled={isSubmitBusy}
            aria-busy={isSubmitBusy}
          >
            {isSubmitBusy ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="login-links" aria-label="Auth links">
          <button type="button" className="text-link" onClick={onCreateAccount} disabled={isSubmitBusy} data-testid="auth-create-account">Create account</button>
          <button type="button" className="text-link" onClick={onForgot} disabled={isSubmitBusy} data-testid="login-forgot-password">Forgot password?</button>
        </div>
    </AuthCard>
  );
}
